import { getExtraHeaders } from "@/lib/queryClient";

/**
 * Shared vocabulary for the in-house comms console (/admin/comms).
 * Shapes follow scripts/comms/API_CONTRACT.md exactly.
 */

export type Channel = "email" | "whatsapp" | "sms" | "push" | "inapp";
export type Audience = "parents" | "students" | "both" | "leads" | "everyone";
export type Category = "learning" | "marketing" | "transactional" | "digest";
export type MatchMode = "all" | "any";
export type FieldType = "number" | "text" | "bool" | "list";
export type RuleOp =
  | "eq" | "ne" | "in" | "gt" | "gte" | "lt" | "lte"
  | "contains" | "exists" | "not_exists";

export const CHANNELS: { id: Channel; label: string }[] = [
  { id: "email", label: "Email" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "sms", label: "SMS" },
  { id: "push", label: "Push notification" },
  { id: "inapp", label: "In-app notice" },
];

/**
 * Channels that address a contact by email address or phone number. Suppressions and
 * "reachable by" segment counts only make sense for these: push and in-app are addressed
 * by the student's user id, not by anything you can type into a box.
 */
export const ADDRESS_CHANNELS: { id: Channel; label: string }[] = CHANNELS.filter(
  c => c.id === "email" || c.id === "whatsapp" || c.id === "sms",
);

/** Push and in-app: subject is the title, the body is delivered as plain text. */
export function isDeviceChannel(channel: string): boolean {
  return channel === "push" || channel === "inapp";
}

export const AUDIENCES: { id: Audience; label: string }[] = [
  { id: "parents", label: "Parents" },
  { id: "students", label: "Students" },
  { id: "both", label: "Parents and students" },
  { id: "leads", label: "Leads (no account yet)" },
  { id: "everyone", label: "Everyone, leads included" },
];

export const CATEGORIES: { id: Category; label: string; hint: string }[] = [
  { id: "learning", label: "Learning", hint: "Progress, study nudges, account and service messages. Respects each contact's topics and frequency." },
  { id: "marketing", label: "Marketing", hint: "Offers and promotions. Needs a marketing opt-in, and never goes to student contacts." },
  { id: "transactional", label: "Transactional", hint: "Receipts, resets and other messages a person asked for. Ignores opt-ins and topics." },
  { id: "digest", label: "Digest", hint: "The weekly progress round-up. Respects the progress topic and the digest slot in Settings." },
];

/** Fallback labels for automation triggers the server did not describe. */
export const TRIGGER_LABELS: Record<string, string> = {
  weekly_digest: "Weekly progress digest",
};

/** Operators the server accepts, narrowed by the field's type. */
export const OPS_BY_TYPE: Record<FieldType, RuleOp[]> = {
  number: ["eq", "ne", "gt", "gte", "lt", "lte", "in"],
  text: ["eq", "ne", "in", "contains", "exists", "not_exists"],
  bool: ["eq"],
  list: ["contains", "exists", "not_exists"],
};

export const OP_LABELS: Record<RuleOp, string> = {
  eq: "is",
  ne: "is not",
  in: "is one of",
  gt: "is greater than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  contains: "contains",
  exists: "is set",
  not_exists: "is not set",
};

/** Ops that take no value input at all. */
export const VALUELESS_OPS: RuleOp[] = ["exists", "not_exists"];

// ---------------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------------

export interface CommsSettings {
  emailProvider: "resend" | "ses";
  fromName: string;
  fromEmail: string;
  replyTo: string;
  dailyEmailCap: number;
  throttlePerMinute: number;
  warmup: { enabled: boolean; startedAt: string | null; startPerDay: number; doubleEveryDays: number };
  quietHours: { start: number; end: number };
  sandbox: { enabled: boolean; allowDomains: string[] };
  defaultCapDays: number;
  whatsappEnabled: boolean;
  smsEnabled: boolean;
  marketingToStudents: boolean;
  trackOpens: boolean;
  trackClicks: boolean;
  alerts: CommsAlertThresholds;
  digest: CommsDigestSlot;
  paused: { at: string; reason: string } | null;
}

export interface CommsAlertThresholds {
  bounceRatePct: number;
  complaintRatePct: number;
  minSample: number;
  queueStallMinutes: number;
}

/** Jamaica time. weekday: 0 = Sunday. */
export interface CommsDigestSlot {
  weekday: number;
  hour: number;
}

export const WEEKDAYS: { id: number; label: string }[] = [
  { id: 0, label: "Sunday" },
  { id: 1, label: "Monday" },
  { id: 2, label: "Tuesday" },
  { id: 3, label: "Wednesday" },
  { id: 4, label: "Thursday" },
  { id: 5, label: "Friday" },
  { id: 6, label: "Saturday" },
];

export function weekdayLabel(weekday: number | null | undefined): string {
  return WEEKDAYS.find(w => w.id === weekday)?.label || "Sunday";
}

export const DEFAULT_ALERTS: CommsAlertThresholds = {
  bounceRatePct: 5,
  complaintRatePct: 0.3,
  minSample: 200,
  queueStallMinutes: 60,
};

export const DEFAULT_DIGEST: CommsDigestSlot = { weekday: 0, hour: 17 };

/** The circuit breaker's own view of the last day. */
export interface GuardStatus {
  paused: { at: string; reason: string } | null;
  last24h: { sent: number; bounced: number; complained: number; bounceRate: number; complaintRate: number };
  queue: { due: number; oldestDueMinutes: number | null };
  breach: string | null;
}

export interface CommsHealth {
  email_sent: number;
  email_bounced: number;
  email_complained: number;
  email_opened: number;
  email_clicked: number;
  whatsapp_sent: number;
  sms_sent: number;
  failed: number;
  queued: number;
  email_sent_today: number;
  bounceRate: number;
  complaintRate: number;
  openRate: number;
  clickRate: number;
}

export interface CommsOverview {
  settings: CommsSettings;
  capToday: number;
  inQuietHours: boolean;
  jamaicaHour: number;
  health: CommsHealth;
  contacts: {
    total: number;
    students: number;
    parents: number;
    email_reachable: number;
    whatsapp_reachable: number;
    bounced: number;
    complained: number;
    unsubscribed: number;
    suppressions: number;
  };
  queue: { due: number; scheduled: number; failed_7d: number };
  activeCampaigns: number;
  automations: { enabled: number; total: number };
  facts: { count: number; refreshedAt: string | null };
  providerReady: { resend: boolean; ses: boolean; whatsapp: boolean; sms: boolean; resendWebhookSecret: boolean };
  recentEvents: Array<{
    id: string;
    sendId: string | null;
    channel: Channel | string;
    type: string;
    provider: string | null;
    url: string | null;
    occurredAt: string;
  }>;
}

export type ContactStatus = "subscribed" | "unsubscribed" | "bounced" | "complained" | "invalid";

export interface CommsContact {
  id: string;
  userId: string | null;
  kind: "student" | "parent" | "lead";
  tags?: string[];
  attributes?: Record<string, unknown>;
  email: string | null;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  emailStatus: ContactStatus | string | null;
  smsStatus: ContactStatus | string | null;
  whatsappStatus: ContactStatus | string | null;
  lastEmailAt: string | null;
  lastWhatsappAt: string | null;
  lastSmsAt: string | null;
  lastEngagedAt: string | null;
  source: string | null;
  updatedAt: string | null;
  marketingOptIn?: boolean | null;
  marketingOptInAt?: string | null;
  /** Leads only: none | pending | confirmed. */
  optInStatus?: string | null;
  pushStatus?: string | null;
  preferences?: {
    channels?: Partial<PreferenceChannels>;
    frequency?: Frequency;
    topics?: Record<string, boolean>;
  } | null;
}

export interface Suppression {
  id: string;
  channel: Channel | string;
  address: string;
  reason: string | null;
  source: string | null;
  createdAt: string;
}

export interface SegmentRule {
  field: string;
  op: RuleOp;
  value?: unknown;
}

export interface Segment {
  id: string;
  name: string;
  description: string | null;
  audience: Audience;
  match: MatchMode;
  rules: SegmentRule[];
  lastCount: number | null;
  lastCountAt: string | null;
  updatedAt: string | null;
}

export interface SegmentField {
  id: string;
  column: string;
  type: FieldType;
  label: string;
  group: string;
  help?: string;
}

export interface SegmentPreset {
  name: string;
  description: string;
  audience: Audience;
  match: MatchMode;
  rules: SegmentRule[];
}

export interface SegmentsResponse {
  segments: Segment[];
  fields: SegmentField[];
  presets: SegmentPreset[];
}

export interface SegmentPreviewSample {
  address: string;
  kind: string;
  firstName: string | null;
  childFirstName: string | null;
  grade: number | null;
  trend: string | null;
  abilityPercentile: number | null;
  weakestTopic: string | null;
}

export interface SegmentPreview {
  count: number;
  sample: SegmentPreviewSample[];
}

export interface Template {
  id: string;
  name: string;
  channel: Channel;
  category: Category;
  subject: string | null;
  previewText: string | null;
  body: string;
  ctaText: string | null;
  ctaUrl: string | null;
  /** WhatsApp only: the approved Twilio Content Template, HX... */
  whatsappContentSid?: string | null;
  /** WhatsApp only: variable names in template order, mapping to {{1}}, {{2}}... */
  whatsappVariables?: string[] | null;
  /** Push and in-app only: the deep link the notice opens. */
  pushUrl?: string | null;
  updatedAt: string | null;
}

export interface TemplatesResponse {
  templates: Template[];
  variables: Array<{ name: string; description: string }>;
}

export type TemplatePreview = { subject?: string; html?: string; text?: string };

export type CampaignStatus = "draft" | "scheduled" | "sending" | "paused" | "sent" | "cancelled";

export interface CampaignStats {
  total: number;
  queued: number;
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  failed: number;
  cancelled: number;
  sandboxed: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  skippedCapped: number;
  /** Kept back at random and never sent, so the campaign can be measured against them. */
  holdout: number;
  /** Reached the conversion goal inside the attribution window. */
  converted: number;
}

export type ConversionGoalId = "login" | "lesson" | "renewal" | "signup" | "none";

export interface ConversionGoal {
  id: ConversionGoalId | string;
  label: string;
  description: string;
}

export interface ConversionVariant {
  variant: string;
  sent: number;
  opened: number;
  clicked: number;
  converted: number;
  rate: number;
}

export interface ConversionReport {
  goal: ConversionGoalId | string;
  windowDays: number;
  sent: number;
  converted: number;
  rate: number;
  holdout: number;
  holdoutConverted: number;
  holdoutRate: number;
  /** null until the holdout is at least 20 people: below that the number means nothing. */
  upliftPoints: number | null;
  variants: ConversionVariant[];
}

/** Experiments and measurement, shared by campaigns and automations. */
export interface ExperimentSettings {
  holdoutPercent: number;
  sendTimeOptimized: boolean;
  conversionGoal: ConversionGoalId | string;
  conversionWindowDays: number;
}

export const HOLDOUT_HELP =
  "kept back at random and never sent, so you can measure what the message actually changed";

export const SEND_TIME_HELP = "send at each child's usual practice time";

export interface Campaign {
  id: string;
  name: string;
  channel: Channel;
  category: Category;
  segmentId: string | null;
  templateId: string | null;
  segmentName?: string | null;
  templateName?: string | null;
  status: CampaignStatus;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  audienceCount: number | null;
  stats: CampaignStats;
  updatedAt: string | null;
  holdoutPercent?: number | null;
  /** Two to four subject lines: each send gets a random variant A/B/C/D. Empty means no test. */
  abSubjects?: string[] | null;
  sendTimeOptimized?: boolean | null;
  conversionGoal?: ConversionGoalId | string | null;
  conversionWindowDays?: number | null;
}

export type SendStatus =
  | "queued" | "sending" | "sent" | "delivered" | "bounced"
  | "complained" | "failed" | "suppressed" | "cancelled" | "sandboxed" | "holdout";

export interface Send {
  id: string;
  channel: Channel | string;
  address: string;
  status: SendStatus | string;
  scheduledFor: string | null;
  provider: string | null;
  providerMessageId: string | null;
  error: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
}

export interface CampaignDetail {
  campaign: Campaign;
  stats: CampaignStats;
  sends: Send[];
  conversions?: ConversionReport | null;
}

export interface Automation {
  id: string;
  name: string;
  trigger: string;
  channel: Channel;
  category: Category;
  audience: Audience;
  templateId: string | null;
  enabled: boolean;
  capPerContactDays: number | null;
  repeatEveryDays: number | null;
  stats: { queued: number; sent: number; failed: number; opened: number; clicked: number; lastRunAt: string | null };
  updatedAt: string | null;
  holdoutPercent?: number | null;
  sendTimeOptimized?: boolean | null;
  conversionGoal?: ConversionGoalId | string | null;
  conversionWindowDays?: number | null;
}

export interface AutomationTrigger {
  id: string;
  label: string;
  description: string;
  defaultAudience: Audience;
  defaultRepeatDays: number | null;
  category: Category;
}

export interface AutomationsResponse {
  automations: Automation[];
  triggers: AutomationTrigger[];
}

// ---------------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------------

export const CAMPAIGN_STATUS_META: Record<CampaignStatus, { label: string; className: string }> = {
  draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
  scheduled: { label: "Scheduled", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  sending: { label: "Sending", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  paused: { label: "Paused", className: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" },
  sent: { label: "Sent", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  cancelled: { label: "Cancelled", className: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};

export const SEND_STATUS_CLASS: Record<string, string> = {
  queued: "bg-muted text-muted-foreground",
  sending: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  delivered: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  bounced: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  complained: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  suppressed: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  sandboxed: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  cancelled: "bg-muted text-muted-foreground",
  holdout: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
};

export const CONTACT_STATUS_CLASS: Record<string, string> = {
  subscribed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  unsubscribed: "bg-muted text-muted-foreground",
  bounced: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  complained: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  invalid: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
};

export const WEBHOOK_URLS: { label: string; url: string; hint: string }[] = [
  {
    label: "Resend",
    url: "https://edufocalacademy.com/api/webhooks/resend",
    hint: "Add this in the Resend dashboard under Webhooks, then paste the signing secret into RESEND_WEBHOOK_SECRET.",
  },
  {
    label: "Amazon SES",
    url: "https://edufocalacademy.com/api/webhooks/ses",
    hint: "Subscribe an SNS topic to this URL and point the SES configuration set at that topic.",
  },
  {
    label: "Twilio status callback",
    url: "https://edufocalacademy.com/api/webhooks/comms-twilio-status",
    hint: "Set this as the status callback on the WhatsApp and SMS senders in the Twilio console.",
  },
];

/** "edufocal.com, edufocalacademy.com" for the sandbox banner and card. */
export function domainList(domains: string[] | null | undefined): string {
  const clean = (domains || []).map(d => d.trim()).filter(Boolean);
  return clean.length === 0 ? "no domains yet" : clean.join(", ");
}

export function parseDomains(raw: string): string[] {
  return raw.split(/[,\s]+/).map(d => d.trim().toLowerCase()).filter(Boolean);
}

export function channelLabel(channel: string): string {
  return CHANNELS.find(c => c.id === channel)?.label || channel;
}

export function audienceLabel(audience: string): string {
  return AUDIENCES.find(a => a.id === audience)?.label || audience;
}

export function categoryLabel(category: string): string {
  return CATEGORIES.find(c => c.id === category)?.label || category;
}

/** Server-supplied trigger labels win; TRIGGER_LABELS fills in the ones it does not describe. */
export function triggerLabel(trigger: string, triggers?: Array<{ id: string; label: string }>): string {
  return triggers?.find(t => t.id === trigger)?.label || TRIGGER_LABELS[trigger] || trigger;
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "0";
  return Number(value).toLocaleString();
}

/** Rates from the server already come through as percentages. */
export function formatRate(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "0%";
  return `${Number(value).toFixed(digits)}%`;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Never";
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return "Never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Never";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return "never";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "never";
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/** "14:00" style label for a Jamaica-time hour of the day. */
export function hourLabel(hour: number | null | undefined): string {
  if (hour === null || hour === undefined || Number.isNaN(hour)) return "n/a";
  const h = ((Math.trunc(hour) % 24) + 24) % 24;
  return `${String(h).padStart(2, "0")}:00`;
}

/** Fetch helper that surfaces the server's own error text verbatim. */
export async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include", headers: getExtraHeaders() });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || `Request failed (${res.status})`;
    try {
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || message;
    } catch {
      /* not JSON, keep the raw text */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

/** Pull the most useful message out of whatever an apiRequest rejection carries. */
export function errorMessage(error: unknown, fallback = "Something went wrong"): string {
  const e = error as any;
  return e?.response?.error || e?.response?.message || e?.message || fallback;
}

/** Turn a raw rule value into what the form input should show. */
export function ruleValueToInput(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

/** Turn a form input back into the payload value the server expects. */
export function inputToRuleValue(raw: string, type: FieldType, op: RuleOp): unknown {
  if (VALUELESS_OPS.includes(op)) return undefined;
  if (op === "in") {
    const parts = raw.split(",").map(s => s.trim()).filter(Boolean);
    return type === "number" ? parts.map(Number).filter(n => !Number.isNaN(n)) : parts;
  }
  if (type === "bool") return raw === "true";
  if (type === "number") {
    const n = Number(raw);
    return raw.trim() === "" || Number.isNaN(n) ? raw : n;
  }
  return raw;
}

/** Group the segment field list the way the server tagged it, keeping server order. */
export function groupFields(fields: SegmentField[]): Array<{ group: string; fields: SegmentField[] }> {
  const groups: Array<{ group: string; fields: SegmentField[] }> = [];
  for (const field of fields) {
    const key = field.group || "Other";
    let bucket = groups.find(g => g.group === key);
    if (!bucket) {
      bucket = { group: key, fields: [] };
      groups.push(bucket);
    }
    bucket.fields.push(field);
  }
  return groups;
}

/** Local datetime-local input value <-> ISO. */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function copyToClipboard(text: string, done: () => void) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(done);
  } else {
    done();
  }
}


// ---------------------------------------------------------------------------------
// Lists, leads, journeys, reports, history
// ---------------------------------------------------------------------------------

export interface CommsList {
  id: string; name: string; description: string | null; kind: string; source: string | null; memberCount: number; createdAt: string; updatedAt: string;
}
export interface LeadStats { leads: number; reachable: number; converted: number; legacy: number; signup_attempts: number; imported: number }
export interface ListsResponse { lists: CommsList[]; leads: LeadStats }
export interface ListMember {
  id: string; kind: string; email: string | null; phone: string | null; first_name: string | null; last_name: string | null;
  email_status: string; whatsapp_status: string; source: string | null; attributes: Record<string, unknown>; tags: string[]; user_id: string | null; added_at: string; member_source: string | null;
}
export interface ListMembersResponse { members: ListMember[]; total: number; page: number; pageSize: number }

export type StepCondition = "always" | "if_not_opened" | "if_not_clicked" | "if_opened" | "if_clicked";
export interface JourneyStep { id?: string; position?: number; name: string | null; delayHours: number; templateId: string | null; channel: Channel | null; condition: StepCondition }
export interface JourneyCounts { enrolled: number; active: number; completed: number; exited: number; sent: number; opened: number; clicked: number; queued: number }
export interface Journey {
  id: string; name: string; description: string | null; trigger: "segment" | "list" | "event"; segmentId: string | null; listId: string | null; eventKey: string | null;
  audience: Audience; channel: Channel; category: Category; enabled: boolean; exitWhenUnmatched: boolean; exitWhenConverted: boolean; reEnrollAfterDays: number | null; respectCap: boolean;
  steps: JourneyStep[]; counts: JourneyCounts; updatedAt: string | null;
}
export interface JourneysResponse { journeys: Journey[]; events: Array<{ id: string; label: string; description: string }>; conditions: Array<{ id: StepCondition; label: string }> }
export interface Enrollment {
  id: string; contact_id: string; user_id: string | null; address: string; channel: string; next_position: number; status: string; next_run_at: string | null; exit_reason: string | null;
  entered_at: string; finished_at: string | null; first_name: string | null; last_name: string | null; kind: string; sent: number; opened: number;
}

export interface CampaignReport {
  campaign: Campaign;
  stats: Record<string, number>;
  rates: { delivered: number; opened: number; clicked: number; bounced: number; complained: number; unsubscribed: number; clickToOpen: number };
  links: Array<{ url: string; clicks: number; uniqueClicks: number }>;
  timeline: Array<{ hour: string; sent: number; opened: number; clicked: number }>;
  domains: Array<{ domain: string; total: number; sent: number; delivered: number; bounced: number; opened: number; clicked: number }>;
  sends: Array<{ id: string; address: string; status: string; subject: string | null; sent_at: string | null; delivered_at: string | null; opened_at: string | null; clicked_at: string | null; error: string | null; provider: string | null; first_name: string | null; last_name: string | null; kind: string | null; user_id: string | null }>;
}

export interface Timeline {
  contacts: Array<CommsContact & { lists: Array<{ id: string; name: string }>; email_status?: string; first_name?: string | null; last_name?: string | null; user_id?: string | null; last_engaged_at?: string | null; created_at?: string }>;
  sends: Array<{ id: string; channel: string; address: string; subject: string | null; status: string; scheduled_for: string | null; sent_at: string | null; delivered_at: string | null; opened_at: string | null; clicked_at: string | null; error: string | null; provider: string | null; campaign_name: string | null; automation_name: string | null; journey_name: string | null; step_name: string | null; step_position: number | null }>;
  events: Array<{ id: string; send_id: string | null; channel: string; type: string; provider: string | null; url: string | null; occurred_at: string }>;
  enrollments: Array<{ id: string; journey_id: string; journey_name: string; status: string; next_position: number; next_run_at: string | null; exit_reason: string | null; entered_at: string; finished_at: string | null; channel: string }>;
}

/** Parse pasted CSV or TSV: first row is the header; email/phone/firstName/lastName are recognised, everything else becomes an attribute. */
export function parseContactsCsv(raw: string): { rows: Array<Record<string, string>>; columns: string[] } {
  const lines = raw.replace(/\r\n?/g, "\n").split("\n").map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return { rows: [], columns: [] };
  const delim = lines[0].includes("\t") ? "\t" : lines[0].includes(";") && !lines[0].includes(",") ? ";" : ",";
  const split = (line: string): string[] => {
    const out: string[] = []; let cur = ""; let quoted = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (quoted && line[i + 1] === '"') { cur += '"'; i++; } else quoted = !quoted; }
      else if (ch === delim && !quoted) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map(x => x.trim());
  };
  const alias: Record<string, string> = { email: "email", "e-mail": "email", emailaddress: "email", phone: "phone", mobile: "phone", whatsapp: "phone", telephone: "phone", firstname: "firstName", first: "firstName", name: "firstName", lastname: "lastName", last: "lastName", surname: "lastName" };
  const columns = split(lines[0]).map(h => { const k = h.toLowerCase().replace(/[^a-z]/g, ""); return alias[k] || h.replace(/[^a-zA-Z0-9_]/g, "_"); });
  const rows = lines.slice(1).map(split).filter(cells => cells.some(Boolean)).map(cells => {
    const row: Record<string, string> = {};
    columns.forEach((c, i) => { if (cells[i]) row[c] = cells[i]; });
    return row;
  });
  return { rows, columns };
}


// ---------------------------------------------------------------------------------
// Layer 3: guard, conversions, consent, uploads
// ---------------------------------------------------------------------------------

/** POST helper for the endpoints that are not wired through apiRequest. */
async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { ...(body === undefined ? {} : { "Content-Type": "application/json" }), ...getExtraHeaders() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || `Request failed (${res.status})`;
    try {
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || message;
    } catch {
      /* not JSON, keep the raw text */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export const GUARD_KEY = ["/api/admin/comms/guard"] as const;

export function fetchGuard(): Promise<GuardStatus> {
  return fetchJson<GuardStatus>("/api/admin/comms/guard");
}

export function pauseSending(reason?: string): Promise<{ ok: boolean }> {
  return postJson<{ ok: boolean }>("/api/admin/comms/guard/pause", { reason: reason?.trim() || undefined });
}

export function resumeSending(): Promise<{ ok: boolean }> {
  return postJson<{ ok: boolean }>("/api/admin/comms/guard/resume", {});
}

export function fetchConversionGoals(): Promise<{ goals: ConversionGoal[] }> {
  return fetchJson<{ goals: ConversionGoal[] }>("/api/admin/comms/conversions/goals");
}

export function fetchCampaignConversions(campaignId: string): Promise<ConversionReport> {
  return fetchJson<ConversionReport>(`/api/admin/comms/campaigns/${campaignId}/conversions`);
}

export function fetchAutomationConversions(automationId: string): Promise<ConversionReport> {
  return fetchJson<ConversionReport>(`/api/admin/comms/automations/${automationId}/conversions`);
}

export function fetchTopics(): Promise<{ topics: TopicOption[] }> {
  return fetchJson<{ topics: TopicOption[] }>("/api/admin/comms/topics");
}

export function sendListOptIn(listId: string, limit?: number): Promise<{ sent: number; skipped: number }> {
  return postJson<{ sent: number; skipped: number }>(`/api/admin/comms/lists/${listId}/send-optin`, limit ? { limit } : {});
}

/** Multipart upload for template images. Never set Content-Type by hand here. */
export async function uploadCommsImage(file: File): Promise<{ url: string; objectName: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch("/api/admin/comms/upload", {
    method: "POST",
    credentials: "include",
    headers: getExtraHeaders(),
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || `Upload failed (${res.status})`;
    try {
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || message;
    } catch {
      /* keep the raw text */
    }
    throw new Error(message);
  }
  return (await res.json()) as { url: string; objectName: string };
}

// ---------------------------------------------------------------------------------
// Consent and the public preference page
// ---------------------------------------------------------------------------------

export type Frequency = "normal" | "weekly" | "minimal";

export const FREQUENCIES: { id: Frequency; label: string; hint: string }[] = [
  { id: "normal", label: "As it happens", hint: "Progress, nudges and news when there is something to say." },
  { id: "weekly", label: "Once a week", hint: "One round-up instead of messages through the week." },
  { id: "minimal", label: "Only the essentials", hint: "Just the things you have to know about the account." },
];

export interface TopicOption {
  id: string;
  label: string;
}

export interface PreferenceChannels {
  email: boolean;
  whatsapp: boolean;
  push: boolean;
}

export interface PreferencesResponse {
  contactId: string;
  kind: "student" | "parent" | "lead" | string;
  firstName: string | null;
  email: string | null;
  phone: string | null;
  marketingOptIn: boolean;
  channels: PreferenceChannels;
  frequency: Frequency;
  topics: Record<string, boolean>;
  topicOptions: TopicOption[];
}

export interface PreferencesUpdate {
  marketingOptIn?: boolean;
  channels?: Partial<PreferenceChannels>;
  frequency?: Frequency;
  topics?: Record<string, boolean>;
}

/** null means the server said 404: the token is not a link we issued. */
export async function fetchPreferences(token: string): Promise<PreferencesResponse | null> {
  const res = await fetch(`/api/comms/preferences?t=${encodeURIComponent(token)}`, { credentials: "omit" });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Could not load your preferences (${res.status})`);
  return (await res.json()) as PreferencesResponse;
}

export async function savePreferences(token: string, body: PreferencesUpdate): Promise<PreferencesResponse> {
  const res = await fetch(`/api/comms/preferences?t=${encodeURIComponent(token)}`, {
    method: "POST",
    credentials: "omit",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || `Could not save your preferences (${res.status})`;
    try {
      const parsed = JSON.parse(text);
      message = parsed.error || parsed.message || message;
    } catch {
      /* keep the raw text */
    }
    throw new Error(message);
  }
  return (await res.json()) as PreferencesResponse;
}

/** j••••@example.com. Values the server already masked are left alone. */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return "";
  if (/[•*]/.test(email)) return email;
  const at = email.indexOf("@");
  if (at < 1) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at);
  const keep = local.slice(0, 1);
  return `${keep}${"•".repeat(Math.max(3, local.length - 1))}${domain}`;
}

/** •••• 4567. Values the server already masked are left alone. */
export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  if (/[•*]/.test(phone)) return phone;
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 4) return phone;
  return `•••• ${digits.slice(-4)}`;
}

// ---------------------------------------------------------------------------------
// Template blocks
//
// The block editor is only a friendlier way to write the markup the server already
// renders (see server/services/comms/render.ts): blank line between paragraphs,
// "## "/"### " headings, "![alt](url|WxH)" images, "> " callouts, "- " bullets, "---"
// rules. Anything it does not recognise round-trips as a paragraph, untouched, so
// {{variables}} and hand-written markup survive a trip through the editor.
// ---------------------------------------------------------------------------------

export type TemplateBlock =
  | { type: "paragraph"; text: string }
  | { type: "heading"; level: 2 | 3; text: string }
  | { type: "image"; url: string; alt: string; width: string; height: string }
  | { type: "button"; text: string; url: string }
  | { type: "callout"; text: string }
  | { type: "bullets"; items: string[] }
  | { type: "divider" };

export const BLOCK_LABELS: Record<TemplateBlock["type"], string> = {
  paragraph: "Paragraph",
  heading: "Heading",
  image: "Image",
  button: "Button",
  callout: "Callout",
  bullets: "Bullets",
  divider: "Divider",
};

export function emptyBlock(type: TemplateBlock["type"]): TemplateBlock {
  switch (type) {
    case "heading": return { type: "heading", level: 2, text: "" };
    case "image": return { type: "image", url: "", alt: "", width: "", height: "" };
    case "button": return { type: "button", text: "", url: "" };
    case "callout": return { type: "callout", text: "" };
    case "bullets": return { type: "bullets", items: [""] };
    case "divider": return { type: "divider" };
    default: return { type: "paragraph", text: "" };
  }
}

// Mirrors the server's own image matcher, including the 2-4 digit size limits.
const IMAGE_RE = /^!\[([^\]]*)\]\((https?:\/\/[^)\s|]+)(?:\|(\d{2,4})(?:x(\d{2,4}))?)?\)$/;
const BUTTON_RE = /^\*\*\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)\*\*$/;
const HEADING_RE = /^(#{2,3})\s+(.+)$/;

/** Markup -> blocks. Never throws: whatever it cannot name becomes a paragraph. */
export function parseBlocks(markup: string): TemplateBlock[] {
  const text = (markup || "").replace(/\r\n?/g, "\n");
  const chunks = text.split(/\n{2,}/).map(c => c.trim()).filter(Boolean);
  const blocks: TemplateBlock[] = [];

  for (const chunk of chunks) {
    const img = IMAGE_RE.exec(chunk);
    if (img) {
      blocks.push({ type: "image", alt: img[1] || "", url: img[2], width: img[3] || "", height: img[4] || "" });
      continue;
    }
    if (/^---+$/.test(chunk)) {
      blocks.push({ type: "divider" });
      continue;
    }
    const btn = BUTTON_RE.exec(chunk);
    if (btn) {
      blocks.push({ type: "button", text: btn[1], url: btn[2] });
      continue;
    }

    const lines = chunk.split("\n");
    const head = HEADING_RE.exec(lines[0]);
    if (head) {
      blocks.push({ type: "heading", level: head[1] === "##" ? 2 : 3, text: head[2] });
      // The server lets a heading carry its paragraph on the following line.
      const rest = lines.slice(1).join("\n").trim();
      if (rest) blocks.push({ type: "paragraph", text: rest });
      continue;
    }
    if (lines.every(l => /^>\s?/.test(l))) {
      blocks.push({ type: "callout", text: lines.map(l => l.replace(/^>\s?/, "")).join("\n") });
      continue;
    }
    if (lines.every(l => /^-\s+/.test(l))) {
      blocks.push({ type: "bullets", items: lines.map(l => l.replace(/^-\s+/, "")) });
      continue;
    }
    blocks.push({ type: "paragraph", text: chunk });
  }

  return blocks;
}

/** Blocks -> markup, in exactly the shape the renderer expects. */
export function serializeBlocks(blocks: TemplateBlock[]): string {
  const parts: string[] = [];
  for (const block of blocks) {
    switch (block.type) {
      case "heading": {
        const text = block.text.trim();
        if (text) parts.push(`${block.level === 3 ? "###" : "##"} ${text}`);
        break;
      }
      case "image": {
        const url = block.url.trim();
        if (!url) break;
        const w = block.width.trim();
        const h = block.height.trim();
        // The renderer reads "|W" or "|WxH". A height on its own has nowhere to go.
        const size = w ? (h ? `|${w}x${h}` : `|${w}`) : "";
        parts.push(`![${block.alt.trim()}](${url}${size})`);
        break;
      }
      case "button": {
        const label = block.text.trim();
        const url = block.url.trim();
        if (label && url) parts.push(`**[${label}](${url})**`);
        break;
      }
      case "callout": {
        const text = block.text.trim();
        if (text) parts.push(text.split("\n").map(l => `> ${l}`).join("\n"));
        break;
      }
      case "bullets": {
        const items = block.items.map(i => i.trim()).filter(Boolean);
        if (items.length) parts.push(items.map(i => `- ${i}`).join("\n"));
        break;
      }
      case "divider":
        parts.push("---");
        break;
      default: {
        const text = block.text.trim();
        if (text) parts.push(text);
        break;
      }
    }
  }
  return parts.join("\n\n");
}

// ---------------------------------------------------------------------------------
// Warm-up: cold leads released into a journey one wave a day
// ---------------------------------------------------------------------------------

export interface WarmupWave { index: number; size: number; enrolled: number; startedAt: string; finishedAt: string | null }
export interface WarmupAudience { listId?: string | null; tag?: string | null; source?: string | null }

export interface WarmupPlan {
  id: string; name: string; journeyId: string; audience: WarmupAudience;
  ramp: number[]; dailyCap: number; minSample: number; bounceRatePct: number; complaintRatePct: number;
  offerCode: string | null; conversionGoal: string; conversionWindowDays: number;
  status: "active" | "paused" | "completed"; pausedReason: string | null;
  waveIndex: number; waves: WarmupWave[]; lastWaveAt: string | null; createdAt: string; updatedAt: string;
}

export interface WaveHealth {
  index: number; queued: number; attempted: number; bounced: number; complained: number; unsubscribed: number;
  opened: number; clicked: number; bounceRate: number; complaintRate: number; settled: boolean; breach: string | null;
}

export interface WarmupPlanStatus {
  plan: WarmupPlan;
  audience: { total: number; remaining: number; suppressed: number; unconfirmed: number };
  nextWaveSize: number; nextWaveAllowedToday: boolean; nextWaveBlockedBy: string | null;
  lastWave: WaveHealth | null;
  totals: { enrolled: number; sent: number; opened: number; clicked: number; bounced: number; complained: number; unsubscribed: number; signups: number; paid: number };
}

export interface OfferDefinition { code: string; type: string; value: number; linkSlug: string; deadlineHours: number | null; description: string }
export interface WarmupResponse { plans: WarmupPlanStatus[]; defaultRamp: number[]; offers: OfferDefinition[] }

export interface WaveResult {
  planId: string; ran: boolean; dryRun: boolean; waveIndex: number; size: number; enrolled: number;
  reason: string | null; paused: boolean; previousWave: WaveHealth | null; sample: string[];
}

export const WARMUP_KEY = ["/api/admin/comms/warmup"] as const;

export function fetchWarmup(): Promise<WarmupResponse> {
  return fetchJson<WarmupResponse>("/api/admin/comms/warmup");
}

export function saveWarmupPlan(body: Record<string, unknown>): Promise<{ plan: WarmupPlanStatus }> {
  return postJson<{ plan: WarmupPlanStatus }>("/api/admin/comms/warmup", body);
}

export function runWarmupWave(id: string, dryRun: boolean, force = false): Promise<WaveResult> {
  return postJson<WaveResult>(`/api/admin/comms/warmup/${encodeURIComponent(id)}/run`, { dryRun, force });
}

export function pauseWarmupPlan(id: string, reason?: string): Promise<{ plan: WarmupPlan }> {
  return postJson<{ plan: WarmupPlan }>(`/api/admin/comms/warmup/${encodeURIComponent(id)}/pause`, { reason: reason?.trim() || undefined });
}

export function resumeWarmupPlan(id: string): Promise<{ plan: WarmupPlan }> {
  return postJson<{ plan: WarmupPlan }>(`/api/admin/comms/warmup/${encodeURIComponent(id)}/resume`, {});
}

export async function deleteWarmupPlan(id: string): Promise<{ ok: boolean }> {
  const res = await fetch(`/api/admin/comms/warmup/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "include", headers: getExtraHeaders() });
  if (!res.ok) throw new Error((await res.text().catch(() => "")) || `Request failed (${res.status})`);
  return (await res.json()) as { ok: boolean };
}

export interface AttributionRow {
  plan: string | null; wave: number | null; offerCode: string | null; journeyId: string | null; step: string | null;
  sent: number; opened: number; clicked: number; bounced: number; complained: number; unsubscribed: number; signups: number; paid: number;
}
export interface AttributionResponse { waves: AttributionRow[]; conversions: Array<Record<string, unknown>> }

export function fetchAttribution(plan?: string): Promise<AttributionResponse> {
  return fetchJson<AttributionResponse>(`/api/admin/comms/attribution${plan ? `?plan=${encodeURIComponent(plan)}` : ""}`);
}
